import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { omit } from 'lodash';
import { compose } from 'redux';

import { SELECTOR_STUB, SidebarView } from 'app-constants';
import { withStreamModule, WithStreamModuleTypes } from 'hoc';
import globalMsgs from 'messages';
import promo from 'modules/promo';
import ui from 'modules/ui';
import UserDevice from 'services/user-device';
import { State as AppState } from 'types';
import Sidebar from './Sidebar';

const {
    actions: { changeSidebarView },
    selectors: { selectSidebarView },
} = ui;
const {
    selectors: { selectAreSportsAvailable },
} = promo;

interface OwnProps {
    isAuthorized: boolean;
    extraTab?: JSX.Element;
}

interface MappedProps {
    activeSidebarView: ReturnType<typeof selectSidebarView>;
    matchIdFullScreen: string | null;
    areSportsAvailable: ReturnType<typeof selectAreSportsAvailable>;
}

interface MappedActions {
    changeSidebarView: typeof changeSidebarView;
}

interface ConnectOwnProps
    extends OwnProps,
        WithStreamModuleTypes.WithStreamModule {}

type Props = OwnProps & MappedProps & MappedActions & WrappedComponentProps;

type TabsData = { [key in SidebarView]: string };

class SidebarContainer extends React.Component<Props> {
    tabsData: TabsData = {
        [SidebarView.betslip]: this.props.intl.formatMessage(
            globalMsgs.bettingSlip
        ),
        [SidebarView.bets]: this.props.intl.formatMessage(globalMsgs.myBets),
        [SidebarView.promo]: this.props.intl.formatMessage(globalMsgs.promo),
        [SidebarView.live]: this.props.intl.formatMessage(globalMsgs.live),
    };

    hiddenWithoutAuth: [SidebarView.bets] = [SidebarView.bets];

    hiddenOnMobile: [SidebarView.promo] = [SidebarView.promo];

    hiddenLiveStreamTabs: { isOpen: SidebarView; isClosed: SidebarView } = {
        isOpen: SidebarView.promo,
        isClosed: SidebarView.live,
    };

    changeActiveTabHandler = (sidebarView: SidebarView) =>
        this.props.changeSidebarView({ sidebarView });

    filterTabsData = (
        isAuthorized: boolean,
        isLiveStreamFullscreen: boolean
    ): Partial<TabsData> => {
        let result: Partial<TabsData> = this.tabsData;

        if (!isAuthorized) {
            result = omit(result, this.hiddenWithoutAuth);
        }

        if (UserDevice.getInstance()?.isDeviceMobile()) {
            result = omit(result, this.hiddenOnMobile);
        }

        return omit(
            result,
            isLiveStreamFullscreen
                ? this.hiddenLiveStreamTabs.isOpen
                : this.hiddenLiveStreamTabs.isClosed
        );
    };

    render() {
        const {
            isAuthorized,
            matchIdFullScreen,
            extraTab,
            activeSidebarView,
            areSportsAvailable,
        } = this.props;

        const tabsData = this.filterTabsData(isAuthorized, !!matchIdFullScreen);

        return React.createElement(Sidebar, {
            matchIdFullScreen,
            activeSidebarView,
            tabsData,
            extraTab,
            changeActiveTabHandler: this.changeActiveTabHandler,
            areSportsAvailable,
        });
    }
}

const makeMapStateToProps = (
    state: AppState,
    ownProps: ConnectOwnProps
): MappedProps => {
    const selectMatchIdFullScreen =
        ownProps.streamModule?.selectors.selectMatchIdFullScreen ??
        SELECTOR_STUB;

    const activeSidebarView = selectSidebarView(state);
    const matchIdFullScreen = selectMatchIdFullScreen(state);
    const areSportsAvailable = selectAreSportsAvailable(state);

    return {
        activeSidebarView,
        matchIdFullScreen,
        areSportsAvailable,
    };
};

const mapDispatchToProps: MappedActions = {
    changeSidebarView,
};

export default compose<React.ComponentClass<OwnProps>>(
    injectIntl,
    withRouter,
    withStreamModule,
    connect<MappedProps, MappedActions, ConnectOwnProps>(
        makeMapStateToProps,
        mapDispatchToProps
    )
)(SidebarContainer);
